import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { createChantier, getChantierTypes, insertChantierDocuments } from '../lib/chantierService';
import { uploadChantierPhoto, uploadTempChantierDocument } from '../lib/fileService';
import { triggerLotExtraction } from '../lib/lotExtractionService';
import { generateTasksFromTemplates, getTemplatesByType } from '../lib/taskTemplateService';
import ChooseChantierTypeStep from './ChooseChantierTypeStep';
import LotsReviewStep from './LotsReviewStep';
import SelectTasksStep from './SelectTasksStep';
import './NewChantierModal.css';

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 Mo

const DOC_CONFIG = {
  planning: {
    label: 'Planning prévisionnel',
    subtitle: 'Planning général ou Gantt par lot',
    hint: 'PDF ou Excel — max 50 Mo',
    required: false,
    accept: '.pdf,.xls,.xlsx',
  },
};

const DOC_ORDER = ['planning'];

const MARCHE_CONFIG = {
  label: 'Marché de travaux',
  subtitle: 'CCAP, CCTP, DPGF ou décomposition des lots',
  hint: 'PDF ou Excel — max 50 Mo par fichier',
  accept: '.pdf,.xls,.xlsx',
};

const MODE_CONFIG = {
  automatisation: {
    label: 'Automatisation',
    subtitle: "Importez vos documents de marché (CCTP, DPGF, planning...) et laissez l'IA générer les lots.",
  },
  templates: {
    label: 'Modèle de projet',
    subtitle: 'Choisissez un type de projet prédéfini pour démarrer rapidement.',
  },
};

function NewChantierModal({ onClose, onCreated }) {
  const { session } = useAuth();
  const userId = session?.user?.id;

  const [step, setStep] = useState(1);
  const [mode, setMode] = useState(null);
  const [name, setName] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  // ── Mode "automatisation" ──
  const [marcheDocs, setMarcheDocs] = useState([]);
  const [uploadingMarche, setUploadingMarche] = useState(false);
  const [docs, setDocs] = useState({});
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [complementaryDocs, setComplementaryDocs] = useState([]);
  const [uploadingComplementary, setUploadingComplementary] = useState(false);

  // ── Mode "templates" ──
  const [typesTree, setTypesTree] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [expandedTypeIds, setExpandedTypeIds] = useState([]);
  const [selectedTypeId, setSelectedTypeId] = useState(null);
  const [taskTemplates, setTaskTemplates] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());

  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [lotsReviewContext, setLotsReviewContext] = useState(null);

  const photoInputRef = useRef(null);

  const canContinueStep1 = name.trim().length > 0 && mode != null;
  const canContinueStep2 =
    (mode === 'automatisation' && marcheDocs.length > 0) ||
    (mode === 'templates' && selectedTypeId != null);

  const selectedType = typesTree.flatMap((p) => p.children).find((c) => c.id === selectedTypeId);

  // Charge l'arbre des types de chantier dès que le mode "templates" est choisi.
  useEffect(() => {
    if (mode !== 'templates') return undefined;
    let cancelled = false;
    setLoadingTypes(true);
    getChantierTypes()
      .then((all) => {
        if (cancelled) return;
        const parents = all.filter((t) => !t.parent_id);
        const children = all.filter((t) => t.parent_id);
        setTypesTree(parents.map((p) => ({ ...p, children: children.filter((c) => c.parent_id === p.id) })));
      })
      .catch((err) => !cancelled && setError(err.message))
      .finally(() => !cancelled && setLoadingTypes(false));
    return () => {
      cancelled = true;
    };
  }, [mode]);

  // Charge les modèles de tâches du type choisi, présélectionnés en totalité.
  useEffect(() => {
    if (mode !== 'templates' || selectedTypeId == null) return undefined;
    let cancelled = false;
    setLoadingTasks(true);
    getTemplatesByType(selectedTypeId)
      .then((templates) => {
        if (cancelled) return;
        setTaskTemplates(templates);
        setSelectedTaskIds(new Set(templates.map((t) => t.id)));
      })
      .catch((err) => !cancelled && setError(err.message))
      .finally(() => !cancelled && setLoadingTasks(false));
    return () => {
      cancelled = true;
    };
  }, [mode, selectedTypeId]);

  function handlePhotoChange(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  }

  function toggleTypeParent(id) {
    setExpandedTypeIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  }

  function selectTypeChild(id) {
    setSelectedTypeId((prev) => (prev === id ? null : id));
  }

  function toggleTask(taskId) {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  }

  function selectAllTasks() {
    setSelectedTaskIds(new Set(taskTemplates.map((t) => t.id)));
  }

  function deselectAllTasks() {
    setSelectedTaskIds(new Set());
  }

  async function pickDocument(type, file) {
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      setError('La taille maximale est de 50 Mo.');
      return;
    }
    setError(null);
    setUploadingDoc(type);
    try {
      const uploaded = await uploadTempChantierDocument(file, type);
      setDocs((prev) => ({ ...prev, [type]: uploaded }));
    } catch (err) {
      setError(`Import impossible : ${err.message}`);
    } finally {
      setUploadingDoc(null);
    }
  }

  function removeDoc(type) {
    setDocs((prev) => {
      const next = { ...prev };
      delete next[type];
      return next;
    });
  }

  async function pickMarcheDocuments(fileList) {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    const validFiles = files.filter((f) => f.size <= MAX_FILE_SIZE);
    if (validFiles.length < files.length) {
      setError(`${files.length - validFiles.length} fichier(s) dépassent 50 Mo et n'ont pas été ajoutés.`);
    } else {
      setError(null);
    }
    if (validFiles.length === 0) return;

    setUploadingMarche(true);
    try {
      const uploaded = await Promise.all(
        validFiles.map((file) => uploadTempChantierDocument(file, 'marche'))
      );
      setMarcheDocs((prev) => [...prev, ...uploaded]);
    } catch (err) {
      setError(`Import impossible : ${err.message}`);
    } finally {
      setUploadingMarche(false);
    }
  }

  function removeMarcheDoc(index) {
    setMarcheDocs((prev) => prev.filter((_, i) => i !== index));
  }

  async function pickComplementaryDocuments(fileList) {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    const validFiles = files.filter((f) => f.size <= MAX_FILE_SIZE);
    if (validFiles.length < files.length) {
      setError(`${files.length - validFiles.length} fichier(s) dépassent 50 Mo et n'ont pas été ajoutés.`);
    } else {
      setError(null);
    }
    if (validFiles.length === 0) return;

    setUploadingComplementary(true);
    try {
      const uploaded = await Promise.all(
        validFiles.map((file) => uploadTempChantierDocument(file, 'complementaire'))
      );
      setComplementaryDocs((prev) => [...prev, ...uploaded]);
    } catch (err) {
      setError(`Import impossible : ${err.message}`);
    } finally {
      setUploadingComplementary(false);
    }
  }

  function removeComplementaryDoc(index) {
    setComplementaryDocs((prev) => prev.filter((_, i) => i !== index));
  }

  async function uploadPhotoIfAny() {
    if (!photoFile) return undefined;
    try {
      return await uploadChantierPhoto(photoFile);
    } catch (err) {
      // Non bloquant, comme sur mobile — mais on log pour pouvoir diagnostiquer.
      console.error('Upload photo error:', err);
      return undefined;
    }
  }

  async function handleCreateAutomatisation() {
    const photoUrl = await uploadPhotoIfAny();
    const chantier = await createChantier(userId, { name: name.trim(), photo_url: photoUrl });

    const docEntries = [...marcheDocs, ...Object.values(docs), ...complementaryDocs];
    const insertedDocuments = await insertChantierDocuments(chantier.id, userId, docEntries);

    let extractionTriggered = false;
    if (insertedDocuments.length > 0) {
      try {
        await triggerLotExtraction(chantier.id, insertedDocuments.map((d) => d.id));
        extractionTriggered = true;
      } catch (err) {
        // Non bloquant — la création du chantier a déjà réussi — mais on log
        // pour pouvoir diagnostiquer un webhook n8n qui ne se déclenche pas.
        console.error('Lot extraction webhook error:', err);
      }
    }

    return { chantier, extractionTriggered, documentIds: insertedDocuments.map((d) => d.id) };
  }

  async function handleCreateFromTemplate() {
    const photoUrl = await uploadPhotoIfAny();
    const chantier = await createChantier(userId, {
      name: name.trim(),
      photo_url: photoUrl,
      chantier_type_id: selectedTypeId,
    });

    const selectedTemplates = taskTemplates.filter((t) => selectedTaskIds.has(t.id));
    if (selectedTemplates.length > 0) {
      await generateTasksFromTemplates(chantier.id, userId, selectedTemplates);
    }

    return chantier;
  }

  async function handleCreate() {
    if (creating) return;
    setCreating(true);
    setError(null);

    try {
      if (mode === 'automatisation') {
        const { chantier, extractionTriggered, documentIds } = await handleCreateAutomatisation();
        if (extractionTriggered) {
          setLotsReviewContext({ chantier, documentIds });
        } else {
          onCreated(chantier);
        }
      } else {
        const chantier = await handleCreateFromTemplate();
        onCreated(chantier);
      }
    } catch (err) {
      setError(`Impossible de créer le projet : ${err.message}`);
    } finally {
      setCreating(false);
    }
  }

  function stepSubtitle() {
    if (step === 1) return 'Informations de base';
    if (step === 2) {
      return mode === 'automatisation' ? 'Documents — analyse automatique par IA' : 'Type de projet';
    }
    if (creating) return 'Création en cours — merci de patienter';
    return mode === 'automatisation' ? 'Récapitulatif avant création' : 'Tâches à créer';
  }

  if (lotsReviewContext) {
    return (
      <div className="ModalOverlay">
        <div className="Modal LotsReviewModal" onClick={(e) => e.stopPropagation()}>
          <LotsReviewStep
            chantierId={lotsReviewContext.chantier.id}
            documentIds={lotsReviewContext.documentIds}
            onFinish={() => onCreated(lotsReviewContext.chantier)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="ModalOverlay" onClick={creating ? undefined : onClose}>
      <div className="Modal" onClick={(e) => e.stopPropagation()}>
        <div className="Modal-header">
          <div>
            <h2 className="Modal-title">Nouveau projet</h2>
            <p className="Modal-subtitle">{stepSubtitle()}</p>
          </div>
          <button
            className="Modal-close"
            onClick={onClose}
            disabled={creating}
            aria-label="Fermer"
          >
            ×
          </button>
        </div>

        <div className="Modal-progress">
          {[1, 2, 3].map((s) => (
            <div key={s} className={`Modal-progressDot${s <= step ? ' is-active' : ''}`} />
          ))}
        </div>

        <div className="Modal-body">
          {error && <p className="Modal-error">{error}</p>}

          {step === 1 && (
            <div className="Modal-form">
              <div
                className="PhotoPicker"
                onClick={() => photoInputRef.current?.click()}
              >
                {photoPreview ? (
                  <img src={photoPreview} alt="Projet" className="PhotoPicker-image" />
                ) : (
                  <div className="PhotoPicker-placeholder">
                    <span>Ajouter une photo</span>
                    <span className="PhotoPicker-sub">Optionnel</span>
                  </div>
                )}
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handlePhotoChange}
                />
              </div>

              <label className="Field">
                <span className="Field-label">
                  Nom du projet <span className="Field-required">*</span>
                </span>
                <input
                  className="Field-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex : Rénovation villa Dupont"
                  autoFocus
                />
              </label>

              <div className="Field">
                <span className="Field-label">Comment créer ce projet ?</span>
                <div className="ModeCard-list">
                  {Object.keys(MODE_CONFIG).map((key) => {
                    const cfg = MODE_CONFIG[key];
                    const isSelected = mode === key;
                    return (
                      <button
                        type="button"
                        key={key}
                        className={`ModeCard${isSelected ? ' is-selected' : ''}`}
                        onClick={() => setMode(key)}
                      >
                        <div className="ModeCard-text">
                          <span className="ModeCard-label">{cfg.label}</span>
                          <span className="ModeCard-subtitle">{cfg.subtitle}</span>
                        </div>
                        <span className={`ModeCard-radio${isSelected ? ' is-checked' : ''}`} />
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {step === 2 && mode === 'automatisation' && (
            <div className="Modal-form">
              <MultiDocCard
                title={MARCHE_CONFIG.label}
                subtitle={MARCHE_CONFIG.subtitle}
                hint={MARCHE_CONFIG.hint}
                accept={MARCHE_CONFIG.accept}
                required
                addLabel="Importer un ou plusieurs documents"
                addLabelMore="Ajouter d'autres documents"
                docs={marcheDocs}
                uploading={uploadingMarche}
                onPick={pickMarcheDocuments}
                onRemove={removeMarcheDoc}
              />

              {DOC_ORDER.map((type) => (
                <DocCard
                  key={type}
                  type={type}
                  doc={docs[type]}
                  uploading={uploadingDoc === type}
                  onPick={(file) => pickDocument(type, file)}
                  onRemove={() => removeDoc(type)}
                />
              ))}

              <MultiDocCard
                title="Documents complémentaires"
                subtitle="Devis, contrats, notes... un ou plusieurs PDF"
                hint="PDF uniquement — max 50 Mo par fichier"
                accept=".pdf"
                addLabel="Importer un ou plusieurs PDF"
                addLabelMore="Ajouter d'autres PDF"
                docs={complementaryDocs}
                uploading={uploadingComplementary}
                onPick={pickComplementaryDocuments}
                onRemove={removeComplementaryDoc}
              />

              <p className="Modal-info">
                Ces documents sont analysés automatiquement après la création du projet.
                Les lots sont générés sans saisie manuelle.
                {marcheDocs.length === 0 && ' Le marché de travaux est requis pour continuer.'}
              </p>
            </div>
          )}

          {step === 2 && mode === 'templates' && (
            <ChooseChantierTypeStep
              tree={typesTree}
              loading={loadingTypes}
              selectedTypeId={selectedTypeId}
              expandedIds={expandedTypeIds}
              onToggleParent={toggleTypeParent}
              onSelectChild={selectTypeChild}
            />
          )}

          {step === 3 && mode === 'automatisation' && (
            <div className="Modal-form">
              <div className="RecapBlock">
                <RecapRow label="Projet" value={name} />
                {photoFile && <RecapRow label="Photo" value="Ajoutée" />}
              </div>

              <div className="RecapBlock">
                <RecapRow
                  label={MARCHE_CONFIG.label}
                  value={marcheDocs.length > 0 ? `${marcheDocs.length} fichier(s)` : '—'}
                  status={
                    marcheDocs.length > 0
                      ? { label: 'Importés — analyse en attente', ok: true }
                      : { label: 'Requis — non importé', ok: false }
                  }
                />
                {DOC_ORDER.map((type) => {
                  const doc = docs[type];
                  const cfg = DOC_CONFIG[type];
                  return (
                    <RecapRow
                      key={type}
                      label={cfg.label}
                      value={doc?.file_name ?? '—'}
                      status={
                        doc
                          ? { label: 'Importé — analyse en attente', ok: true }
                          : cfg.required
                          ? { label: 'Requis — non importé', ok: false }
                          : { label: 'Non importé', ok: null }
                      }
                    />
                  );
                })}
                <RecapRow
                  label="Documents complémentaires"
                  value={complementaryDocs.length > 0 ? `${complementaryDocs.length} fichier(s)` : '—'}
                  status={
                    complementaryDocs.length > 0
                      ? { label: 'Importés — analyse en attente', ok: true }
                      : { label: 'Non importés', ok: null }
                  }
                />
              </div>

              <p className="Modal-info">
                Après création, votre marché de travaux sera analysé et les lots générés.
              </p>
            </div>
          )}

          {step === 3 && mode === 'templates' && (
            <SelectTasksStep
              chantierTypeName={selectedType?.name ?? ''}
              tasks={taskTemplates}
              loading={loadingTasks}
              selectedTaskIds={selectedTaskIds}
              onToggle={toggleTask}
              onSelectAll={selectAllTasks}
              onDeselectAll={deselectAllTasks}
            />
          )}
        </div>

        <div className="Modal-footer">
          {step > 1 && (
            <button className="Modal-btn Modal-btn-secondary" onClick={() => setStep(step - 1)} disabled={creating}>
              Retour
            </button>
          )}
          {step < 3 && (
            <button
              className="Modal-btn Modal-btn-primary"
              onClick={() => setStep(step + 1)}
              disabled={(step === 1 && !canContinueStep1) || (step === 2 && !canContinueStep2)}
            >
              Continuer
            </button>
          )}
          {step === 3 && (
            <button className="Modal-btn Modal-btn-primary" onClick={handleCreate} disabled={creating}>
              {creating ? (
                <span className="Modal-btn-loading">
                  <span className="Modal-btnSpinner" />
                  Création en cours…
                </span>
              ) : (
                'Créer le projet'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function DocCard({ type, doc, uploading, onPick, onRemove }) {
  const cfg = DOC_CONFIG[type];
  const fileInputRef = useRef(null);

  return (
    <div className="DocCard">
      <div className="DocCard-header">
        <div className="DocCard-info">
          <span className="DocCard-title">{cfg.label}</span>
          <span className="DocCard-sub">{cfg.subtitle}</span>
        </div>
        <span className={`DocCard-badge${cfg.required ? ' is-required' : ''}`}>
          {cfg.required ? 'Requis' : 'Optionnel'}
        </span>
      </div>

      {uploading ? (
        <div className="UploadZone">
          <span className="UploadZone-text">Import en cours…</span>
        </div>
      ) : doc ? (
        <div className="UploadZone is-filled">
          <span className="UploadZone-text">{doc.file_name}</span>
          <button className="UploadZone-remove" onClick={onRemove}>×</button>
        </div>
      ) : (
        <div
          className="UploadZone is-dashed"
          onClick={() => fileInputRef.current?.click()}
        >
          <span className="UploadZone-text">Importer un document</span>
          <span className="UploadZone-hint">{cfg.hint}</span>
          <input
            ref={fileInputRef}
            type="file"
            accept={cfg.accept}
            hidden
            onChange={(e) => {
              onPick(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
        </div>
      )}
    </div>
  );
}

export function MultiDocCard({
  title,
  subtitle,
  hint,
  accept,
  required,
  addLabel,
  addLabelMore,
  docs,
  uploading,
  onPick,
  onRemove,
}) {
  const fileInputRef = useRef(null);

  return (
    <div className="DocCard">
      <div className="DocCard-header">
        <div className="DocCard-info">
          <span className="DocCard-title">{title}</span>
          <span className="DocCard-sub">{subtitle}</span>
        </div>
        <span className={`DocCard-badge${required ? ' is-required' : ''}`}>
          {required ? 'Requis' : 'Optionnel'}
        </span>
      </div>

      {docs.map((doc, index) => (
        <div className="UploadZone is-filled" key={`${doc.file_name}-${index}`}>
          <span className="UploadZone-text">{doc.file_name}</span>
          <button className="UploadZone-remove" onClick={() => onRemove(index)}>×</button>
        </div>
      ))}

      {uploading ? (
        <div className="UploadZone">
          <span className="UploadZone-text">Import en cours…</span>
        </div>
      ) : (
        <div
          className="UploadZone is-dashed"
          onClick={() => fileInputRef.current?.click()}
        >
          <span className="UploadZone-text">{docs.length > 0 ? addLabelMore : addLabel}</span>
          <span className="UploadZone-hint">{hint}</span>
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            multiple
            hidden
            onChange={(e) => {
              onPick(e.target.files);
              e.target.value = '';
            }}
          />
        </div>
      )}
    </div>
  );
}

function RecapRow({ label, value, status }) {
  const statusClass = status?.ok === true ? 'is-ok' : status?.ok === false ? 'is-missing' : '';
  return (
    <div className="RecapRow">
      <span className="RecapRow-label">{label}</span>
      <div>
        {value !== '—' && <div className="RecapRow-value">{value}</div>}
        {status && <div className={`RecapRow-status ${statusClass}`}>{status.label}</div>}
      </div>
    </div>
  );
}

export default NewChantierModal;
